import * as Yup from 'yup';
import { TFunction } from 'i18next';

/**
 * Returns Yup validation schema for the tableDelimiter field.
 * Validates that the delimiter is unique and not a substring of tableRowDelimiter.
 *
 * @param t - Translation function from react-i18next
 * @returns Yup string schema with conditional validation for Table custom field type
 */
export const getTableDelimiterValidation = (t: TFunction) => {
  return Yup.string()
    .required(t('validation:required'))
    .test(
      'unique-delimiter',
      t('settings:customFields.validation.delimitersMustBeUnique'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { tableRowDelimiter } = this.parent;
        return !value || !tableRowDelimiter || value !== tableRowDelimiter;
      }
    )
    .test(
      'not-substring',
      t('settings:customFields.validation.delimiterCannotBeSubstring'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { tableRowDelimiter } = this.parent;
        if (!value || !tableRowDelimiter) return true;
        return !value.includes(tableRowDelimiter) && !tableRowDelimiter.includes(value);
      }
    );
};

/**
 * Returns Yup validation schema for the tableRowDelimiter field.
 * Validates that the delimiter is unique and not a substring of tableDelimiter.
 *
 * @param t - Translation function from react-i18next
 * @returns Yup string schema with conditional validation for Table custom field type
 */
export const getTableRowDelimiterValidation = (t: TFunction) => {
  return Yup.string()
    .required(t('validation:required'))
    .test(
      'unique-delimiter',
      t('settings:customFields.validation.delimitersMustBeUnique'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { tableDelimiter } = this.parent;
        return !value || !tableDelimiter || value !== tableDelimiter;
      }
    )
    .test(
      'not-substring',
      t('settings:customFields.validation.delimiterCannotBeSubstring'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { tableDelimiter } = this.parent;
        if (!value || !tableDelimiter) return true;
        return !value.includes(tableDelimiter) && !tableDelimiter.includes(value);
      }
    );
};

/**
 * Returns Yup validation schema for the productRelationDelimiter field.
 * Validates that the delimiter is unique and not a substring of productRelationBrandCodeDelimiter.
 *
 * @param t - Translation function from react-i18next
 * @returns Yup string schema with conditional validation for Product Relation custom field type
 */
export const getProductRelationDelimiterValidation = (t: TFunction) => {
  return Yup.string()
    .required(t('validation:required'))
    .test(
      'unique-delimiter',
      t('settings:customFields.validation.delimitersMustBeUnique'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { productRelationBrandCodeDelimiter } = this.parent;
        return (
          !value ||
          !productRelationBrandCodeDelimiter ||
          value !== productRelationBrandCodeDelimiter
        );
      }
    )
    .test(
      'not-substring',
      t('settings:customFields.validation.delimiterCannotBeSubstring'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { productRelationBrandCodeDelimiter } = this.parent;
        if (!value || !productRelationBrandCodeDelimiter) return true;
        return (
          !value.includes(productRelationBrandCodeDelimiter) &&
          !productRelationBrandCodeDelimiter.includes(value)
        );
      }
    );
};

/**
 * Returns Yup validation schema for the productRelationBrandCodeDelimiter field.
 * Validates that the delimiter is unique and not a substring of productRelationDelimiter (when provided).
 *
 * @param t - Translation function from react-i18next
 * @param required - Whether the field should be required (default: false)
 * @returns Yup string schema with conditional validation for Product Relation custom field type
 */
export const getProductRelationBrandCodeDelimiterValidation = (t: TFunction, required = false) => {
  const baseSchema = Yup.string()
    .test(
      'unique-delimiter',
      t('settings:customFields.validation.delimitersMustBeUnique'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { productRelationDelimiter } = this.parent;
        return !value || !productRelationDelimiter || value !== productRelationDelimiter;
      }
    )
    .test(
      'not-substring',
      t('settings:customFields.validation.delimiterCannotBeSubstring'),
      // eslint-disable-next-line func-names
      function (value) {
        // eslint-disable-next-line react/no-this-in-sfc
        const { productRelationDelimiter } = this.parent;
        if (!value || !productRelationDelimiter) return true;
        return (
          !value.includes(productRelationDelimiter) && !productRelationDelimiter.includes(value)
        );
      }
    );

  // Make the schema nullable when not required to allow null/empty values
  return required ? baseSchema.required(t('validation:required')) : baseSchema.nullable();
};
